import { Dispatch, Middleware } from 'redux';
import { call, put, spawn, takeEvery } from 'redux-saga/effects';

// import { companies$get$xs } from '@/api/companies';
import { filterPresets$get$base } from '@/api/filter-presets';
import { languages$get$base } from '@/api/platform-data';
import { tags$get$base, tags$getTranslations } from '@/api/tags';
import { LanguageCode } from '@/i18n/languages';
import { Language } from '@/types/common';
import { FilterPreset } from '@/types/filter-presets';
import { eventFilterKeys, FilterPriceType } from '@/types/filters';
import { IdName } from '@/types/general';
import { TranslationSource } from '@/types/platform-data';
import { storageKeys } from '@/utils/constants';
import { Cookies } from '@/utils/cookies';
import { _window } from '@/utils/ssr-window';

import {
    CalendarFiltersActionTypes,
    calendarFiltersKey,
    CalendarFiltersState,
    filtersLoad,
    filtersLoadSuccess,
} from './calendar-filters.actions';
import { Filters, FiltersStored } from './calendar-filters.types';

function* filtersLoad$(action: ReturnType<typeof filtersLoad>) {
    try {
        const language = Cookies.get(storageKeys.prefLanguage) as LanguageCode;
        const tags: IdName[] = yield call(tags$get$base, 'Event');

        const languages: Language[] = yield call(languages$get$base);

        const languagesTranslated = languages
            .filter((lang) => lang.code !== 'xx')
            .map((lang) => ({
                ...lang,
                title: lang.translations?.find((trans) => trans.language === language)?.nom ?? lang.title,
            }));

        if (tags) {
            const tagIds = tags.map((tag) => tag.id);
            const tagTranslations: TranslationSource[] = yield call(tags$getTranslations, language, tagIds);

            if (tagTranslations) {
                for (const tag of tags) {
                    const translation = tagTranslations.find((trans) => trans.entity === tag.id)?.nom;
                    tag.name = translation ?? tag.name;
                }
            }
        }

        const prices: FilterPriceType = ['free', 'paid'];

        const filters: Filters = {
            city: action.payload.cities.map((item) => ({ key: 'city', value: item.id, display: item.name })),
            event_type: tags.map((item) => ({ key: 'event_type', value: item.id, display: item.name })),
            language: languagesTranslated.map((item) => ({ key: 'language', value: item.code, display: item.title })),
            price: prices.map((item) => ({ key: 'price', value: item, display: item })),
            countryCode: action.payload.countryCode,
        };

        const filterPreset: FilterPreset | null = action.payload.filterPresetId
            ? yield call(filterPresets$get$base, action.payload.filterPresetId)
            : null;

        yield put(filtersLoadSuccess({ filters: filters, filterPreset: filterPreset }));
    } catch {}
}

function* filtersLoadSaga() {
    yield takeEvery(CalendarFiltersActionTypes.filtersLoad, filtersLoad$);
}

export function* calendarFiltersEffects() {
    yield spawn(filtersLoadSaga);
}

export const calendarFiltersStorageMw: Middleware<Dispatch, { [calendarFiltersKey]: CalendarFiltersState }> =
    (api) => (next) => (action) => {
        const result = next(action);
        if (
            [
                CalendarFiltersActionTypes.filterSelect,
                CalendarFiltersActionTypes.filterDeselect,
                CalendarFiltersActionTypes.filtersReset,
            ].includes(action.type)
        ) {
            const newValue = api.getState()[calendarFiltersKey].filtersSelected;
            const stored = {} as FiltersStored;
            for (const fKey of eventFilterKeys) {
                stored[fKey] = newValue[fKey].map((item) => item.value);
            }
            _window?.localStorage?.setItem(`${calendarFiltersKey}_${newValue.countryCode}`, JSON.stringify(stored));
        }
        return result;
    };
